<template>
  <div>
    <v-card class="pa-2">
      <v-container
        fill-height
        grid-list-lg
      >
        <v-row
          v-if="receipt && receipt.status === RECEIPT_PENDING"
          wrap
        >
          <v-col cols="4">
            <template v-if="products.length">
              <h2 class="text-h4 mb-2 font-weight-light">{{ $t('Summary') }}</h2>

              <v-list class="list-receipt-summary super-dense text-caption">
                <v-divider class="pa-1" />
                <v-list-item
                  v-for="(product, i) in products"
                  :key="i"
                  inactive
                >
                  <v-list-item-avatar class="text-left">{{ `${product.qty}x` }}</v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="text-caption">{{ product.display }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    {{ product.totalInclVat | money }}
                  </v-list-item-action>
                </v-list-item>
                <v-divider class="mt-2" />
              </v-list>

              <v-list class="list-receipt-summary super-dense text-body-2">
                <v-list-item>
                  <v-list-item-content>{{ $t('Subtotal') }}</v-list-item-content>
                  <!-- <v-list-item-content class="justify-end text-right">{{ receipt.totalProductsInclVat + (-receipt.totalDiscountInclVat) | money }}</v-list-item-content> -->
                  <v-list-item-content class="justify-end text-right">{{ receipt.totalOriginalProductsInclVat || 0 | money }}</v-list-item-content>
                </v-list-item>

                <v-list-item v-if="receipt.totalDiscountInclVat">
                  <v-list-item-content>
                    <template v-if="receipt.totalDiscountInclVat > 0">{{ $t('Addition') }}</template>
                    <template v-else>{{ $t('Discount') }}</template>
                  </v-list-item-content>
                  <v-list-item-content class="justify-end text-right">{{ receipt.roundedTotalDiscountInclVat | money }}</v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>{{ $t('Total') }}</v-list-item-content>
                  <v-list-item-content class="justify-end text-right">{{ receipt.totalInclVat | money }}</v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content class="font-weight-bold">{{ $t('Total') }} {{ `(${$t('Rounded').toLowerCase()})` }}</v-list-item-content>
                  <v-list-item-content class="justify-end text-right">{{ receipt.totalInclVatRounded | money }}</v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>{{ $t('VAT') }}</v-list-item-content>
                  <v-list-item-content class="justify-end text-right">{{ receipt.totalVat | money }}</v-list-item-content>
                </v-list-item>

                <template v-if="receipt.transactions && receipt.transactions.length">
                  <v-divider class="mt-2 mb-3" />
                  <v-slide-y-transition group>
                    <v-list-item
                      v-for="transaction in receipt.transactions"
                      :key="transaction.uuid"
                      :style="{height: transaction.paymentMethod.code == 'vs-giftcard' ? `40px`: `30px`}"
                      class="mb-2 pl-0"
                    >
                      <v-fade-transition>
                        <v-list-item-icon
                          class="ma-0 mr-2"
                          style="width: 28px"
                        >
                          <v-btn
                            v-if="!transaction.isRefund"
                            :disabled="$wait.is('printing receipt') || $wait.is('completing receipt') || !!receipt.printed"
                            :loading="$wait.is(`removing transaction ${transaction.uuid}`)"
                            class="ma-0"
                            icon
                            ripple
                            small
                            @click="removeTransaction(transaction)"
                          >
                            <v-icon size="14px">fal fa-trash-alt</v-icon>
                          </v-btn>
                        </v-list-item-icon>
                      </v-fade-transition>
                      <v-list-item-content>
                        {{ transaction.isRefund ? $t('Refund') : `${_.get(transaction, 'paymentMethod.name')} ${_.get(transaction, 'payload.code', '')}` }}
                        <template v-if="_.get(transaction, 'payload.note')">{{ `(${transaction.payload.note})` }}</template>
                        <br>
                        <small>{{ transaction.created | moment("D MMMM YYYY HH:mm") }}</small>
                      </v-list-item-content>
                      <v-list-item-content class="justify-end">{{ transaction.totalInclVat | money }}</v-list-item-content>
                    </v-list-item>
                  </v-slide-y-transition>
                </template>

                <v-divider class="mt-2 mb-2" />

                <v-list-item>
                  <v-list-item-content class="font-weight-bold">{{ $t('Outstanding Balance') }}</v-list-item-content>
                  <v-list-item-content class="justify-end text-right">{{ receipt.outstandingBalance | money }}</v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>{{ $t('Number of {0}', [$t('Articles')]) }}: {{ itemCount }}</v-list-item-content>
                </v-list-item>

              </v-list>
            </template>
          </v-col>

          <v-col
            class="text-center"
            cols="1"
          >
            <v-divider vertical />
          </v-col>

          <v-col cols="7">
            <template v-if="$wait.is('fetching receipt')">
              <v-row
                class="relative text-center fill-height align-center"
                wrap
              >
                <v-col cols="12">
                  <v-progress-circular
                    :size="30"
                    color="primary"
                    indeterminate
                  />
                </v-col>
              </v-row>
            </template>

            <span v-show="!$wait.is('fetching receipt') && receipt.outstandingBalance > 0">
              <v-row
                class="relative text-center fill-height"
                wrap
              >
                <v-col
                  class="d-flex align-center text-left"
                  cols="6"
                >
                  <h4 class="text-h5 font-weight-light">{{ $t('Outstanding Balance') }}</h4>
                </v-col>
                <v-col
                  class="d-flex align-center"
                  cols="6"
                >
                  <v-text-field
                    id="inpt-outstanding"
                    :value="receipt.outstandingBalance | money"
                    class="ml-12"
                    filled
                    flat
                    hide-details
                    outlined
                    readonly
                    reverse
                    style="font-size: 30px"
                  />
                </v-col>

                <v-col cols="12">
                  <h3 class="text-h6 text-center mb-2">{{ $t('Choose a {0}', [$t('Payment Method').toLowerCase()]) }}</h3>
                  <v-progress-circular
                    v-if="$wait.is('fetching paymentmethods')"
                    color="primary"
                    indeterminate
                  />
                  <v-row
                    class="justify-center"
                    wrap
                  >
                    <v-col
                      v-for="(paymentMethod, key) in paymentMethods.sticky"
                      :key="paymentMethod.id"
                      :order="key * 5"
                      cols="4"
                    >
                      <select-payment-method-dialog
                        :payment-method-object="paymentMethod"
                        @done="lastPaymentMethodCode = $event.code"
                        @open-drawer="openDrawer($event)"
                      />
                    </v-col>

                    <!-- button to open dialog with non-sticky payment methods -->
                    <v-col
                      cols="4"
                      order="2"
                    >
                      <v-dialog
                        v-model="showOtherPaymentsDialog"
                        width="unset"
                      >
                        <template v-slot:activator="{ on }">
                          <v-btn
                            block
                            class="ml-0 text-subtitle-1"
                            color="grey"
                            dark
                            depressed
                            large
                            style="height: 55px;"
                            v-on="on"
                          >
                            <v-icon
                              class="mr-4"
                              dark
                              left
                            >fal fa-receipt</v-icon>
                            {{ $t('Other') }}
                          </v-btn>
                        </template>

                        <v-card>
                          <v-card-text class="pt-2 pb-8">
                            <v-row
                              class="justify-center"
                              wrap
                            >
                              <v-col cols="12">
                                <h3 class="text-h6 text-center">{{ $t('Choose a {0}', [$t('Payment Method').toLowerCase()]) }}</h3>
                              </v-col>
                              <v-col
                                v-for="(paymentMethod) in paymentMethods.other"
                                :key="paymentMethod.id"
                                cols="4"
                              >
                                <select-payment-method-dialog
                                  :payment-method-object="paymentMethod"
                                  @done="lastPaymentMethodCode = $event.code; showOtherPaymentsDialog = false"
                                  @open-drawer="openDrawer($event)"
                                />
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </v-dialog>
                    </v-col>
                    <v-btn
                      :disabled="!!receipt.printed || !!(receipt.transactions && receipt.transactions.length)"
                      absolute
                      bottom
                      class="ml-0"
                      depressed
                      right
                      style="bottom: -28px;"
                      text
                      @click="goBack"
                    >
                      <v-icon
                        dark
                        left
                      >fal fa-angle-left</v-icon>
                      {{ $t('Go Back') }}
                    </v-btn>
                  </v-row>
                </v-col>
              </v-row>
            </span>

            <template v-if="!$wait.is('fetching receipt') && receipt.outstandingBalance <= 0">
              <v-row
                class="relative text-center fill-height"
                wrap
              >
                <v-col
                  cols="12"
                  text-center
                >
                  <h1 class="text-h4">{{ $t('The order has been fully paid for') }}</h1>
                  <h3
                    v-show="refundAmount < 0"
                    class="text-h6 mt-6"
                  >{{ $t('To refund') }}
                    <v-chip
                      class="text-h6"
                      dark
                      label
                    >{{ refundAmount | money }}</v-chip>
                  </h3>
                  <v-divider class="mt-12 mb-12" />
                  <receipt-printer
                    :receipt-object="receipt"
                    @printed="completeReceipt"
                  >
                    <template slot="activator">
                      <v-btn
                        :loading="$wait.is('printing receipt')"
                        color="primary"
                        class="ml-0 white--text"
                        depressed
                      >
                        <v-icon
                          dark
                          left
                        >print</v-icon>
                        {{ $t('Print') }}
                      </v-btn>
                    </template>
                  </receipt-printer>

                  <v-btn
                    :disabled="$wait.is('printing receipt')"
                    :loading="$wait.is('completing receipt')"
                    class="ml-0 white--text"
                    color="green"
                    depressed
                    @click="completeReceipt"
                  >
                    <v-icon
                      dark
                      left
                    >fal fa-check</v-icon>
                    {{ $t('Finish') }}
                  </v-btn>

                  <v-btn
                    :disabled="!!receipt.printed || !!(receipt.transactions && receipt.transactions.length && !hasOnlyRefunds)"
                    absolute
                    bottom
                    class="ml-0"
                    depressed
                    right
                    style="bottom: -28px;"
                    text
                    @click="goBack"
                  >
                    <v-icon
                      left
                      dark
                    >fal fa-angle-left</v-icon>
                    {{ $t('Go Back') }}
                  </v-btn>
                </v-col>
                <v-col
                  cols="10"
                  offset="1"
                  text-center
                >
                  <countdown
                    v-if="showCompletionCountdown"
                    ref="countdown"
                    :left-time="countDownForAutomaticCompletion"
                    @finish="completeReceipt">
                    <template
                      v-slot:process="{ timeObj }"
                    >
                      <div v-show="timeObj.leftTime < 60000">
                        <p>
                          {{ $t(`Receipt will be automatically completed`) }} in
                          <span class="font-weight-bold">
                            <template v-if="timeObj.m != '00'">{{ Math.floor(timeObj.org.m) }}
                              {{ timeObj.m == '01' ? $t(`Minute`).toLowerCase() : $t(`Minutes`).toLowerCase() }}
                              {{ $t('And').toLowerCase() }}
                            </template>
                            {{ Math.ceil(timeObj.org.s) }} {{ $t(`Seconds`).toLowerCase() }}
                          </span>
                        </p>
                        <v-progress-linear
                          :value="((countDownForAutomaticCompletion - timeObj.leftTime) / countDownForAutomaticCompletion) * 100"
                          color="primary"
                          height="5"
                          rounded
                        />
                      </div>
                    </template>
                  </countdown>
                </v-col>
              </v-row>
            </template>

          </v-col>
        </v-row>
        <v-row
          v-if="receipt && receipt.status !== RECEIPT_PENDING"
          wrap
        >
          <v-col
            class="text-center"
            cols="12"
          >
            <v-icon
              class="mb-5"
              size="50px"
            >fal fa-check-circle</v-icon>
            <h1 class="text-h4">{{ $t('The order has been completed') }}</h1>
            <v-btn
              :to="'/logout'"
              class="mt-3"
              color="primary"
              text
            >
              {{ $t('Logout') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <drawer-opener
      ref="drawer"
      @hook:mounted="checkDrawerNeedsOpening"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import ReceiptPrinter from '@/components/receipts/ReceiptPrinter'
import DrawerOpener from '@/components/DrawerOpener'
import paymentMethodService from '@/services/payment-methods'
import Flag from '@/components/Flag'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import { RECEIPT_PENDING } from '@/utils/statuses/receipt'
import * as promiseRetry from 'promise-retry'
import SelectPaymentMethodDialog from '@/components/dialogs/SelectPaymentMethodDialog'

export default {
  name: 'CartPayment',

  components: {
    ReceiptPrinter,
    DrawerOpener,
    Flag,
    LanguageSwitcher,
    SelectPaymentMethodDialog
  },

  props: {
    showCompletionCountdown: {
      required: true,
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    lastPaymentMethodCode: null,
    countDownForAutomaticCompletion: 600000, // 10 minutes
    paidAmount: null,
    payload: {},
    paymentMethods: [],
    RECEIPT_PENDING,
    showAddTransactionKeyboard: {},
    showOtherPaymentsDialog: false
  }),

  computed: {
    ...mapState('RECEIPT', ['receipt']),
    ...mapGetters('RECEIPT', ['products', 'itemCount', 'refundAmount', 'isOnlyRefund']),
    hasOnlyRefunds () {
      if (!this.receipt.transactions) return false
      if (this.receipt.transactions.length !== 1) return false
      return this.receipt.transactions[0].isRefund
    }
  },

  watch: {
    'refundAmount': {
      handler (refundAmount) {
        this.checkDrawerNeedsOpening()
      }
    }
  },

  created () {
    this.fetchPaymentMethods()
  },

  methods: {
    checkDrawerNeedsOpening () {
      // Check if money should be refunded
      // Don't open the cash drawer if last chosen transaction was cash. Drawer should be already open.
      if (this.refundAmount < 0 && this.lastPaymentMethodCode !== 'cash') {
        this.openDrawer(this.$t('Refund'))
      }
    },
    completeReceipt () {
      this.$wait.start('completing receipt')
      return this.$store.dispatch('RECEIPT/COMPLETE_RECEIPT')
        .then(data => {
          this.helpers.toastSuccess(data.message, { timeout: 5000 })
        })
        .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
        .finally(() => this.$wait.end('completing receipt'))
    },

    fetchPaymentMethods () {
      this.$wait.start('fetching paymentmethods')
      promiseRetry((retry, number) => paymentMethodService.all().catch(retry))
        .then((data) => {
          this.paymentMethods = data.data.payment_methods
          this.$wait.end('fetching paymentmethods')
        })
        .catch((err) => console.log(err))
    },

    openDrawer (reason = null) {
      if (reason) this.$refs.drawer.reasonForOpening = reason
      this.$refs.drawer.openDrawer()
    },

    removeTransaction (transaction) {
      this.$wait.start(`removing transaction ${transaction.uuid}`)
      return this.$store.dispatch('RECEIPT/REMOVE_TRANSACTION', transaction.uuid)
        .then((data) => this.helpers.toastSuccess(data.message))
        .finally(() => this.$wait.end(`removing transaction ${transaction.uuid}`))
    },

    goBack () {
      this.$emit('closed')
      if (this.showCompletionCountdown) {
        this.$refs.countdown.stopCountdown()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.list-receipt-summary .v-list__item {
  height: 30px !important;
  padding: 0;
}
</style>

<style lang="scss">
#inpt-outstanding {
  margin-top: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/checkout/CartPayment.vue